import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  sub?: string;
  firstName?: string;
  lastName?: string;
  customerId?: number;
  userId?: string;
  exp?: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  customerId: number | null;
  userId: number | null;
  login: (token: string) => void;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [userId, setUserId] = useState<number | null>(null);

  // Check token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        const isValid = decoded.exp ? decoded.exp * 1000 > Date.now() : false;
        
        if (isValid) {
          setIsAuthenticated(true);
          setUsername(decoded.sub || localStorage.getItem('username'));
          setFirstName(decoded.firstName || localStorage.getItem('firstName'));
          setLastName(decoded.lastName || localStorage.getItem('lastName'));
          setCustomerId(decoded.customerId || Number(localStorage.getItem('customerId')) || null);
          const storedUserId = decoded.userId ? Number(decoded.userId) : Number(localStorage.getItem('userId')) || null;
          setUserId(storedUserId);
        } else {
          logout();
        }
      } catch {
        logout();
      }
    }
  }, []);

  const login = (token: string) => {
    localStorage.setItem('token', token);
    
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const user = decoded.sub || '';
      const first = decoded.firstName || '';
      const last = decoded.lastName || '';
      const custId = decoded.customerId;
      const uid = decoded.userId ? Number(decoded.userId) : null;
      
      if (user) localStorage.setItem('username', user);
      if (first) localStorage.setItem('firstName', first);
      if (last) localStorage.setItem('lastName', last);
      if (custId) localStorage.setItem('customerId', String(custId));
      if (uid) localStorage.setItem('userId', String(uid));
      
      setIsAuthenticated(true);
      setUsername(user);
      setFirstName(first);
      setLastName(last);
      setCustomerId(custId || null);
      setUserId(uid);
    } catch (error) {
      console.error('Failed to parse token:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('customerId');
    localStorage.removeItem('userId');
    
    setIsAuthenticated(false);
    setUsername(null);
    setFirstName(null);
    setLastName(null);
    setCustomerId(null);
    setUserId(null);
  };

  const getToken = () => localStorage.getItem('token');

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      username,
      firstName,
      lastName,
      customerId,
      userId,
      login,
      logout,
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
